import type { PointerEvent as ReactPointerEvent } from 'react';
import { useRef, useState } from 'react';

import { cn } from '@april9au/react-common/utils';

import type { DragTargetsSlice, Point, Zone } from '@/chat/activity';
import { markerCount, targetZones, zoneAt } from '@/chat/activity';
import { PrismicMedia } from '@/components/ui/media';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';
import { HintArea, hintArea, pointAt, Stage, ZoneButton } from './stage';

/**
 * Place the flags. The flags move as one group, since a patrolled area is the
 * patch between the pair. The pair stands one above the other: the beach in
 * the course's aerial photos runs up the picture, so flags along the sand read
 * vertically, the way the answer artwork draws them. They wait in a dock at the bottom of the picture and
 * are dragged with pointer events, which cover mouse, pen and touch.
 * `touch-none` on the stage stops a drag turning into a page scroll. The draft
 * still holds one point per marker, with every slot set to the same point.
 *
 * The dock sits inside the picture on purpose. A handle that moved between two
 * containers would remount mid-gesture and drop the pointer capture the drag
 * depends on.
 *
 * A tap on a target zone (or Enter on it) places the group on that zone's
 * centre. Wrong zones such as the rip can be chosen too, so their feedback can
 * explain them. `hint` dims the picture outside a loose box around the correct
 * targets.
 */

export type DragSurfaceProps = {
  slice: DragTargetsSlice;
  /** One slot per marker; `null` means "still in the dock". */
  points: Array<Point | null>;
  armed: boolean;
  revealed: boolean;
  hint: boolean;
  onChange: (points: Array<Point | null>) => void;
};

/** Where the group waits in the dock, in percent of the picture (the pair's centre). */
const DOCK: Point = { x: 14, y: 72 };

export function DragSurface({ slice, points, armed, revealed, hint, onChange }: DragSurfaceProps) {
  const { chat } = useStrings();
  const stage = useRef<HTMLDivElement>(null);
  // The ref is what gates a move, not the state: a fast drag fires
  // `pointermove` before React has re-rendered, and a state gate would drop
  // those frames.
  const held = useRef(false);
  const [dragging, setDragging] = useState(false);
  const zones = targetZones(slice);
  const total = markerCount(slice);
  // Every slot carries the same point, so the first one is the group's.
  const placed = points.find((point) => point !== null) ?? null;
  const at = placed ?? DOCK;
  const landed = placed === null ? null : zoneAt(zones, placed);
  const area = hint && !revealed ? hintArea(zones) : null;

  const place = (point: Point) => {
    onChange(Array.from({ length: total }, () => point));
  };

  const startDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if (!armed) {
      return;
    }

    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    held.current = true;
    setDragging(true);

    // Leaving the dock, the group jumps to the finger. Once placed it stays
    // put until the finger moves, so a tap cannot nudge it.
    if (stage.current && placed === null) {
      place(pointAt(stage.current, event));
    }
  };

  const moveDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if (!held.current || !stage.current) {
      return;
    }

    place(pointAt(stage.current, event));
  };

  const endDrag = () => {
    held.current = false;
    setDragging(false);
  };

  return (
    <>
      <Stage
        image={slice.primary.image}
        revealImage={slice.primary.reveal_image}
        revealed={revealed}
        interactive={false}
        stageRef={stage}
      >
        {zones.map((zone, index) => (
          <ZoneButton
            key={`${zone.label}-${index}`}
            zone={zone}
            label={formatString(chat.chat_activity_target, { label: zone.label })}
            onSelect={() => place(centre(zone))}
            disabled={!armed}
            highlighted={revealed && zone.correct}
          />
        ))}

        {area ? <HintArea zone={area} label={chat.chat_activity_hint_area} /> : null}

        {/* Rendered before the handle so the dock label sits under it. */}
        {placed === null && !revealed ? (
          <span className="absolute start-[26%] bottom-2 flex h-9 items-center rounded-full bg-black/50 px-3 text-[11px] font-bold tracking-[0.1em] text-white uppercase backdrop-blur-[2px]">
            {chat.chat_activity_tray}
          </span>
        ) : null}

        <button
          type="button"
          data-autopilot="marker"
          data-autopilot-placed={placed === null ? undefined : ''}
          disabled={!armed}
          aria-label={
            placed === null
              ? chat.chat_activity_markers
              : formatString(chat.chat_activity_markers_placed, {
                  label: landed?.label ?? chat.chat_activity_elsewhere,
                })
          }
          style={{ left: `${at.x}%`, top: `${at.y}%` }}
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className={cn(
            // The handle's centre — the patch between the two flags — is the
            // placed point, so a pair placed near the top or bottom of the
            // picture keeps both flags in frame.
            'absolute flex min-h-11 min-w-11 -translate-x-1/2 -translate-y-1/2 touch-none flex-col items-center justify-center rounded-2xl',
            'focus-visible:ring-surf-blue focus-visible:ring-[3px] focus-visible:outline-none',
            armed && 'cursor-grab',
            dragging && 'cursor-grabbing',
          )}
        >
          {Array.from({ length: total }, (_, index) =>
            slice.primary.marker_image.url === null || slice.primary.marker_image.url === undefined ? (
              <span key={index} aria-hidden className="bg-surf-red my-0.5 size-5 rounded-full" />
            ) : (
              <PrismicMedia
                key={index}
                image={slice.primary.marker_image}
                alt=""
                priority
                placeholder="transparent"
                draggable={false}
                className={cn(
                  'pointer-events-none h-11 w-auto drop-shadow-md',
                  // Spaced so the pair reads as the two ends of an area.
                  index > 0 && 'mt-3',
                  // Settle once on release. A dragged group never animates,
                  // or it would lag behind the finger.
                  placed !== null && !dragging && 'activity-marker-settle',
                )}
              />
            ),
          )}
        </button>
      </Stage>

      <p aria-live="polite" className="sr-only">
        {placed === null
          ? chat.chat_activity_markers
          : formatString(chat.chat_activity_markers_placed, {
              label: landed?.label ?? chat.chat_activity_elsewhere,
            })}
      </p>
    </>
  );
}

const centre = (zone: Zone): Point => ({ x: zone.x + zone.w / 2, y: zone.y + zone.h / 2 });
